import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  RestaurantDiningRoomDTO,
  RestaurantDiningRoomPage,
  RestaurantDiningRoomForm,
} from '../../../types/restaurant/RestaurantDiningRoomTypes';

export const emptyDiningRoomForm: RestaurantDiningRoomForm = {
  name: '',
  description: '',
  displayOrder: 1,
};

interface State {
  list: RestaurantDiningRoomDTO[];
  page: RestaurantDiningRoomPage | null;
  form: RestaurantDiningRoomForm;
  loading: boolean;
  updating: boolean;
  error: string;
  saving: boolean;
  modalOpen: boolean;
}

const initialState: State = {
  list: [],
  page: null,
  form: emptyDiningRoomForm,
  loading: false,
  updating: false,
  error: '',
  saving: false,
  modalOpen: false,
};

const slice = createSlice({
  name: 'restaurantDiningRoom',
  initialState,
  reducers: {
    setDiningRooms(state, action: PayloadAction<RestaurantDiningRoomDTO[]>) {
      state.list = action.payload;
    },
    setDiningRoomPage(
      state,
      action: PayloadAction<RestaurantDiningRoomPage | null>
    ) {
      state.page = action.payload;
      state.list = action.payload?.content ?? [];
    },
    setDiningRoomLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setDiningRoomUpdating(state, action: PayloadAction<boolean>) {
      state.updating = action.payload;
    },
    setDiningRoomError(state, action: PayloadAction<string>) {
      state.error = action.payload;
    },
    resetDiningRoomResults(state) {
      state.list = [];
      state.page = null;
      state.loading = false;
      state.updating = false;
      state.error = '';
    },
    setDiningRoomSaving(state, action: PayloadAction<boolean>) {
      state.saving = action.payload;
    },
    openDiningRoomCreate(state) {
      state.form = { ...emptyDiningRoomForm };
      state.modalOpen = true;
    },
    openDiningRoomEdit(state, action: PayloadAction<RestaurantDiningRoomDTO>) {
      const room = action.payload;
      state.form = {
        id: room.id,
        name: room.name,
        description: room.description ?? '',
        displayOrder: room.displayOrder,
        active: room.active,
      };
      state.modalOpen = true;
    },
    closeDiningRoomModal(state) {
      state.form = { ...emptyDiningRoomForm };
      state.modalOpen = false;
    },
    updateDiningRoomField(
      state,
      action: PayloadAction<{
        field: keyof RestaurantDiningRoomForm;
        value: RestaurantDiningRoomForm[keyof RestaurantDiningRoomForm];
      }>
    ) {
      state.form = {
        ...state.form,
        [action.payload.field]: action.payload.value,
      };
    },
  },
});

export const {
  setDiningRooms,
  setDiningRoomPage,
  setDiningRoomLoading,
  setDiningRoomUpdating,
  setDiningRoomError,
  resetDiningRoomResults,
  setDiningRoomSaving,
  openDiningRoomCreate,
  openDiningRoomEdit,
  closeDiningRoomModal,
  updateDiningRoomField,
} = slice.actions;

export default slice.reducer;
